import { Deferred, Head, Link, router, usePage } from "@inertiajs/react"
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyData = Record<string, any>
import AddIcon from "@mui/icons-material/Add"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import CloseIcon from "@mui/icons-material/Close"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Container,
    Divider,
    Grid,
    IconButton,
    Paper,
    Skeleton,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers"
import dayjs from "dayjs"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Control, Controller, SubmitHandler, useFieldArray, useForm, useWatch } from "react-hook-form"
import { formatErrorMessages, translateErrorMessage } from "@/lib/formErrors"
import { formatFpcId, formatMoney } from "@/lib/format"
import { computeEntryBenefits } from "@/lib/generalInfoBenefits"
import { getYears, parseDateOnly } from "@/lib/time"
import { IEmployee } from "@/models/employee"
import { GeneralInfo, GeneralInfoEntry, GeneralInfoEntryAdditionalRetention, IGeneralInfo } from "@/models/generalInfo"

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
    is_edit: boolean
    general_info_base?: { id: number; name: string; report_date: string } | null
    general_info?: IGeneralInfo | null  // undefined = deferred (edit loading), null = new
    employees?: IEmployee[]             // deferred — undefined on initial render
    back_params: Record<string, string | null>
    errors?: Record<string, string[]>
}

interface EntryProps {
    control: Control<IGeneralInfo>
    fieldIndex: number
    employees: IEmployee[]
    employeesLoading: boolean
    reportDate: unknown
    onRemove: (index: number) => void
    onEmployeeChange: (value: IEmployee | null, index: number) => void
    onSalaryChange: (value: number, index: number) => void
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function StatField({ label, value }: { label: string; value: number }) {
    return (
        <TextField
            label={label}
            value={formatMoney(value)}
            size="small"
            fullWidth
            variant="filled"
            slotProps={{ input: { readOnly: true, disableUnderline: true } }}
            sx={{ "& .MuiFilledInput-root": { borderRadius: 1 } }}
        />
    )
}

function SectionLabel({ children }: { children: string }) {
    return (
        <Box sx={{ gridColumn: "1 / -1", mt: 1 }}>
            <Typography
                variant="overline"
                color="text.secondary"
                sx={{ fontWeight: "bold", letterSpacing: 1.2, fontSize: "0.65rem" }}
            >
                {children}
            </Typography>
            <Divider sx={{ mt: 0.25 }} />
        </Box>
    )
}

// ─── Salary Field ────────────────────────────────────────────────────────────

function SalaryField({ control, fieldIndex, onSalaryChange }: {
    control: Control<IGeneralInfo>
    fieldIndex: number
    onSalaryChange: (value: number, index: number) => void
}) {
    const { t } = useTranslation()
    const salary = useWatch({ control, name: `general_info_entries.${fieldIndex}.salary` })
    const [display, setDisplay] = useState(() => (salary > 0 ? String(salary) : ""))

    return (
        <TextField
            label={t("generalInfos.form.salary")}
            slotProps={{ htmlInput: { inputMode: "decimal" } }}
            size="small"
            fullWidth
            value={display}
            onChange={e => {
                setDisplay(e.target.value)
                onSalaryChange(Number(e.target.value) || 0, fieldIndex)
            }}
        />
    )
}

// ─── Entry Accordion ─────────────────────────────────────────────────────────

function EntryAccordion({
    control,
    fieldIndex,
    employees,
    employeesLoading,
    reportDate,
    onRemove,
    onEmployeeChange,
    onSalaryChange,
}: EntryProps) {
    const employee = useWatch({ control, name: `general_info_entries.${fieldIndex}.employee` })
    const salary = useWatch({ control, name: `general_info_entries.${fieldIndex}.salary` })

    const { fields: retentionFields, append: appendRetention, remove: removeRetention, swap: swapRetention } = useFieldArray({
        control,
        name: `general_info_entries.${fieldIndex}.general_info_entry_additional_retentions`,
    })

    const { t } = useTranslation()
    const years = getYears(reportDate as Date | null, employee?.join_date || null)

    const summaryName = employee
        ? `${employee.name} ${employee.last_name}`
        : t("generalInfos.form.noEmployeeSelected")

    return (
        <Accordion defaultExpanded disableGutters variant="outlined" sx={{ "&:before": { display: "none" } }}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                    flexDirection: "row-reverse",
                    px: 2,
                    minHeight: 52,
                    "& .MuiAccordionSummary-content": { my: 0, alignItems: "center", gap: 1.5, ml: 1 },
                }}
            >
                {employee ? (
                    <Chip
                        label={formatFpcId(employee.fpc_id)}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ fontFamily: "monospace", fontSize: "0.72rem", flexShrink: 0 }}
                    />
                ) : (
                    <Chip label="—" size="small" variant="outlined" sx={{ flexShrink: 0 }} />
                )}

                <Typography
                    sx={{
                        flexGrow: 1,
                        fontWeight: employee ? 500 : 400,
                        color: employee ? "text.primary" : "text.disabled",
                        fontSize: "0.9rem",
                    }}
                >
                    {summaryName}
                </Typography>

                {employee && (
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, mr: 1 }}>
                        {years.toFixed(1)} {t("generalInfos.form.yrs")}
                    </Typography>
                )}

                {salary > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, flexShrink: 0, mr: 1 }}>
                        {formatMoney(salary)}
                    </Typography>
                )}

                <Tooltip title={t("common.removeEntry")}>
                    <IconButton
                        size="small"
                        color="error"
                        onClick={e => { e.stopPropagation(); onRemove(fieldIndex) }}
                        sx={{ flexShrink: 0 }}
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 1.5 }}>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(12, 1fr)",
                        gap: 2,
                        alignItems: "start",
                    }}
                >
                    {/* ── Employee + Salary ── */}
                    <Box sx={{ gridColumn: { xs: "span 12", md: "span 5" } }}>
                        <Controller
                            control={control}
                            name={`general_info_entries.${fieldIndex}.employee`}
                            render={({ field: { value } }) => (
                                <Autocomplete
                                    options={employees}
                                    loading={employeesLoading}
                                    value={value}
                                    isOptionEqualToValue={(op, v) => op.id === v.id}
                                    getOptionLabel={op =>
                                        `${formatFpcId(op.fpc_id)} ${op.name} ${op.last_name} (${getYears(reportDate as Date | null, op.join_date).toFixed(1)} yrs)`
                                    }
                                    onChange={(_, data) => onEmployeeChange(data, fieldIndex)}
                                    renderInput={params => (
                                        <TextField {...params} label={t("common.employee")} size="small" />
                                    )}
                                />
                            )}
                        />
                    </Box>

                    <Box sx={{ gridColumn: { xs: "span 6", md: "span 2" } }}>
                        <SalaryField
                            control={control}
                            fieldIndex={fieldIndex}
                            onSalaryChange={onSalaryChange}
                        />
                    </Box>

                    <Box sx={{ gridColumn: { xs: "span 6", md: "span 2" } }}>
                        <Controller
                            control={control}
                            name={`general_info_entries.${fieldIndex}.daily_salary`}
                            render={({ field: { value } }) => (
                                <StatField label={t("generalInfos.form.dailySalary")} value={value} />
                            )}
                        />
                    </Box>

                    <Box sx={{ gridColumn: { xs: "span 6", md: "span 3" } }}>
                        <Controller
                            control={control}
                            name={`general_info_entries.${fieldIndex}.yearly_salary`}
                            render={({ field: { value } }) => (
                                <StatField label={t("generalInfos.form.yearlySalary")} value={value} />
                            )}
                        />
                    </Box>

                    {/* ── Benefits ── */}
                    <SectionLabel>{t("generalInfos.form.benefits")}</SectionLabel>

                    {(
                        [
                            ["vacations", "generalInfos.form.vacations"],
                            ["vacations_bonus", "generalInfos.form.vacationsBonus"],
                            ["easter_royalty", "generalInfos.form.easterRoyalty"],
                            ["first_bonus", "generalInfos.form.firstBonus"],
                            ["second_bonus", "generalInfos.form.secondBonus"],
                            ["bonus", "generalInfos.form.totalBonus"],
                        ] as const
                    ).map(([name, labelKey]) => (
                        <Box key={name} sx={{ gridColumn: { xs: "span 6", md: "span 2" } }}>
                            <Controller
                                control={control}
                                name={`general_info_entries.${fieldIndex}.${name}`}
                                render={({ field: { value } }) => (
                                    <StatField label={t(labelKey)} value={value} />
                                )}
                            />
                        </Box>
                    ))}

                    {/* ── Retentions ── */}
                    <SectionLabel>{t("generalInfos.form.employeeRetentions")}</SectionLabel>

                    {(
                        [
                            ["afp_retention", "generalInfos.form.afpRetention"],
                            ["sfs_retention", "generalInfos.form.sfsRetention"],
                            ["contribution_retention", "generalInfos.form.contributionRetention"],
                        ] as const
                    ).map(([name, labelKey]) => (
                        <Box key={name} sx={{ gridColumn: { xs: "span 6", md: "span 2" } }}>
                            <Controller
                                control={control}
                                name={`general_info_entries.${fieldIndex}.${name}`}
                                render={({ field: { value } }) => (
                                    <StatField label={t(labelKey)} value={value} />
                                )}
                            />
                        </Box>
                    ))}

                    {/* Additional retentions */}
                    {retentionFields.length > 0 && (
                        <Box sx={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: 1 }}>
                            {retentionFields.map((retField, retIndex) => (
                                // Composite key (id+index) forces React to remount
                                // a row whenever its position changes. Without the
                                // index suffix, swap() would move existing instances
                                // and the Controllers' name props would change in
                                // place — RHF's useController has known issues
                                // re-subscribing on name change, leaving the inputs
                                // unresponsive. With only the index, swap() reuses
                                // the same instances and the visual reorder is lost.
                                <Box key={`${retField.id}-${retIndex}`} sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                                    <Controller
                                        control={control}
                                        name={`general_info_entries.${fieldIndex}.general_info_entry_additional_retentions.${retIndex}.is_active`}
                                        render={({ field: { value, onChange } }) => (
                                            <Tooltip title={t("generalInfos.form.active")}>
                                                <Checkbox size="small" checked={!!value} onChange={e => onChange(e.target.checked)} />
                                            </Tooltip>
                                        )}
                                    />
                                    <Controller
                                        control={control}
                                        name={`general_info_entries.${fieldIndex}.general_info_entry_additional_retentions.${retIndex}.amount`}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                label={t("generalInfos.form.amount")}
                                                type="number"
                                                size="small"
                                                sx={{ width: 160 }}
                                            />
                                        )}
                                    />
                                    <Controller
                                        control={control}
                                        name={`general_info_entries.${fieldIndex}.general_info_entry_additional_retentions.${retIndex}.description`}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                label={t("generalInfos.form.description")}
                                                size="small"
                                                sx={{ flexGrow: 1 }}
                                            />
                                        )}
                                    />
                                    <Tooltip title={t("generalInfos.form.moveUp")}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={retIndex === 0}
                                                onClick={() => swapRetention(retIndex, retIndex - 1)}
                                            >
                                                <KeyboardArrowUpIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={t("generalInfos.form.moveDown")}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={retIndex === retentionFields.length - 1}
                                                onClick={() => swapRetention(retIndex, retIndex + 1)}
                                            >
                                                <KeyboardArrowDownIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={t("common.remove")}>
                                        <IconButton size="small" color="error" onClick={() => removeRetention(retIndex)}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            ))}
                        </Box>
                    )}

                    <Box sx={{ gridColumn: "1 / -1" }}>
                        <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => appendRetention(new GeneralInfoEntryAdditionalRetention())}
                        >
                            {t("generalInfos.form.addRetention")}
                        </Button>
                    </Box>

                    {/* ── Employer Payments ── */}
                    <SectionLabel>{t("generalInfos.form.employerPayments")}</SectionLabel>

                    {(
                        [
                            ["afp_payment", "generalInfos.form.afpPayment"],
                            ["sfs_payment", "generalInfos.form.sfsPayment"],
                            ["arl_payment", "generalInfos.form.arlPayment"],
                            ["infotep_payment", "generalInfos.form.infotepPayment"],
                            ["contribution_payment", "generalInfos.form.contributionPayment"],
                            ["tss_payment", "generalInfos.form.tssPayment"],
                        ] as const
                    ).map(([name, labelKey]) => (
                        <Box key={name} sx={{ gridColumn: { xs: "span 6", md: "span 2" } }}>
                            <Controller
                                control={control}
                                name={`general_info_entries.${fieldIndex}.${name}`}
                                render={({ field: { value } }) => (
                                    <StatField label={t(labelKey)} value={value} />
                                )}
                            />
                        </Box>
                    ))}
                </Box>
            </AccordionDetails>
        </Accordion>
    )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function GeneralInfoFormPage() {
    const { is_edit: isEdit, general_info_base, general_info, employees: rawEmployees, back_params, errors: serverErrors } = usePage<Props>().props
    const { t } = useTranslation()
    const employees = rawEmployees ?? []
    const employeesLoading = rawEmployees === undefined
    const deferredLoading = general_info === undefined || employeesLoading
    const originalInfo = useRef<IGeneralInfo | null>(null)

    const { control, reset, handleSubmit, setValue, getValues } = useForm<IGeneralInfo>({
        defaultValues: general_info
            ? { ...general_info }
            : general_info_base
            ? { ...new GeneralInfo(), ...general_info_base, report_date: parseDateOnly(general_info_base.report_date) }
            : new GeneralInfo(),
    })

    const { fields: entryFields, append: entryAppend, remove: entryRemove } = useFieldArray({
        name: "general_info_entries",
        control,
    })

    const reportDate = useWatch({ control, name: "report_date" })

    const [submitting, setSubmitting] = useState(false)
    const [localErrors, setLocalErrors] = useState<Record<string, string[]>>({})

    const generalInfoKey = `${general_info?.id}-${general_info?.updated_at}`
    useEffect(() => {
        if (general_info) {
            originalInfo.current = general_info
            reset(general_info)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generalInfoKey])

    useEffect(() => {
        if (serverErrors && Object.keys(serverErrors).length > 0) {
            setLocalErrors(serverErrors)
        }
    }, [serverErrors])

    const handleAddEntry = () => entryAppend(new GeneralInfoEntry())
    const handleRemoveEntry = (index: number) => entryRemove(index)

    // Recomputes everything derived from the salary and the employee's
    // seniority. Must run on every input that feeds it (salary, employee,
    // report date), otherwise amounts for the previous employee/date are
    // persisted.
    const recomputeEntry = (index: number, salary: number, joinDate: Date | null | undefined) => {
        const benefits = computeEntryBenefits(salary, getValues().report_date, joinDate)

        setValue(`general_info_entries.${index}.salary`, salary)
        setValue(`general_info_entries.${index}.daily_salary`, benefits.daily_salary)
        setValue(`general_info_entries.${index}.yearly_salary`, benefits.yearly_salary)
        setValue(`general_info_entries.${index}.vacations`, benefits.vacations)
        setValue(`general_info_entries.${index}.vacations_bonus`, benefits.vacations_bonus)
        setValue(`general_info_entries.${index}.easter_royalty`, benefits.easter_royalty)
        setValue(`general_info_entries.${index}.first_bonus`, benefits.first_bonus)
        setValue(`general_info_entries.${index}.second_bonus`, benefits.second_bonus)
        setValue(`general_info_entries.${index}.bonus`, benefits.bonus)

        setValue(`general_info_entries.${index}.afp_retention`, benefits.afp_retention)
        setValue(`general_info_entries.${index}.sfs_retention`, benefits.sfs_retention)
        setValue(`general_info_entries.${index}.contribution_retention`, benefits.contribution_retention)

        setValue(`general_info_entries.${index}.afp_payment`, benefits.afp_payment)
        setValue(`general_info_entries.${index}.sfs_payment`, benefits.sfs_payment)
        setValue(`general_info_entries.${index}.arl_payment`, benefits.arl_payment)
        setValue(`general_info_entries.${index}.infotep_payment`, benefits.infotep_payment)
        setValue(`general_info_entries.${index}.contribution_payment`, benefits.contribution_payment)
        setValue(`general_info_entries.${index}.tss_payment`, benefits.tss_payment)
    }

    const recomputeAllEntries = () => {
        getValues().general_info_entries.forEach((entry, index) => {
            recomputeEntry(index, entry.salary, entry.employee?.join_date)
        })
    }

    const handleEmployeeChange = (value: IEmployee | null, index: number) => {
        setValue(`general_info_entries.${index}.employee`, value)
        setValue(`general_info_entries.${index}.employee_id`, value?.id || 0)
        recomputeEntry(index, getValues().general_info_entries[index].salary, value?.join_date)
    }

    const handleSalaryChange = (value: number, index: number) => {
        const entry = getValues().general_info_entries[index]
        recomputeEntry(index, value, entry.employee?.join_date)
    }

    const buildSubmitData = (values: IGeneralInfo): IGeneralInfo => {
        const submitData: IGeneralInfo = JSON.parse(JSON.stringify(values))
        if (isEdit && originalInfo.current) {
            const currentIds = submitData.general_info_entries.map(e => e.id).filter(id => id > 0)
            originalInfo.current.general_info_entries.forEach(original => {
                if (!currentIds.includes(original.id)) {
                    submitData.general_info_entries.push({ ...original, _destroy: true })
                } else {
                    const entry = submitData.general_info_entries.find(x => x.id === original.id)
                    if (entry) {
                        const currentRetIds = entry.general_info_entry_additional_retentions
                            .map(r => r.id).filter(id => id > 0)
                        original.general_info_entry_additional_retentions.forEach(r => {
                            if (!currentRetIds.includes(r.id)) {
                                entry.general_info_entry_additional_retentions.push({ ...r, _destroy: true })
                            }
                        })
                    }
                }
            })
        }
        return submitData
    }

    const submit = (values: IGeneralInfo, stay = false) => {
        setSubmitting(true)
        setLocalErrors({})

        const submitData = buildSubmitData(values)

        const cleanBackParams = Object.fromEntries(
            Object.entries(back_params).filter(([, v]) => v != null)
        ) as Record<string, string>
        const backQuery = new URLSearchParams(cleanBackParams).toString()
        const url = isEdit
            ? `/general_infos/${general_info!.id}?${backQuery}`
            : `/general_infos?${backQuery}`

        const originalUrl = window.location.pathname + window.location.search

        const onError = (errors: Record<string, string | string[]>) => {
            window.history.replaceState(window.history.state, "", originalUrl)
            setLocalErrors(
                Object.fromEntries(
                    Object.entries(errors).map(([k, v]) => [k, Array.isArray(v) ? v : [v]])
                )
            )
            setSubmitting(false)
        }
        const onSuccess = () => setSubmitting(false)
        if (isEdit && stay) {
            router.patch(url, { general_info: submitData, stay: 1 } as AnyData, {
                only: ["general_info"],
                preserveState: true,
                preserveScroll: true,
                preserveUrl: true,
                onError,
                onSuccess,
            })
        } else if (isEdit) {
            router.patch(url, { general_info: submitData } as AnyData, { onError, onSuccess })
        } else if (stay) {
            router.post(url, { general_info: submitData, stay: 1 } as AnyData, { preserveScroll: true, onError, onSuccess })
        } else {
            router.post(url, { general_info: submitData } as AnyData, {
                onError,
                onSuccess: () => { sessionStorage.removeItem('scroll_general_infos'); onSuccess() },
            })
        }
    }

    const onSubmit: SubmitHandler<IGeneralInfo> = values => submit(values, false)
    const onSubmitAndStay: SubmitHandler<IGeneralInfo> = values => submit(values, true)

    const cleanBackParams = Object.fromEntries(
        Object.entries(back_params).filter(([, v]) => v != null)
    ) as Record<string, string>
    const backUrl = `/general_infos?${new URLSearchParams(cleanBackParams).toString()}`
    const errorMessages = formatErrorMessages(t, localErrors)

    return (
        <>
            <Head title={isEdit ? t("generalInfos.form.editTitle") : t("generalInfos.form.newTitle")} />

            <Box
                sx={{
                    bgcolor: "background.default",
                    borderBottom: 1,
                    borderColor: "divider",
                }}
            >
                <Container maxWidth="xl" sx={{ py: 1.25 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1.5 } }}>
                    <Tooltip title={t("generalInfos.form.backTooltip")}>
                        <IconButton LinkComponent={Link} href={backUrl} size="small">
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "flex" } }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", flexGrow: 1, display: { xs: "none", sm: "block" } }}>
                        {isEdit ? t("generalInfos.form.editTitle") : t("generalInfos.form.newTitle")}
                    </Typography>
                    <Box sx={{ flexGrow: { xs: 1, sm: 0 } }} />
                    <Button
                        color="inherit"
                        LinkComponent={Link}
                        href={backUrl}
                        disabled={submitting || deferredLoading}
                        size="small"
                    >
                        {t("common.cancel")}
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSubmit(onSubmitAndStay)}
                        disabled={submitting || deferredLoading}
                    >
                        {t("common.saveAndContinue")}
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleSubmit(onSubmit)}
                        disabled={submitting || deferredLoading}
                    >
                        {submitting ? t("common.saving") : t("common.save")}
                    </Button>
                </Box>
                </Container>
            </Box>

            <Container maxWidth="xl" sx={{ py: 4 }}>
                {errorMessages.length > 0 && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {errorMessages.join(" · ")}
                    </Alert>
                )}

                {/* ── Name + Date ── */}
                <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
                    <Grid container spacing={2} sx={{ alignItems: "center" }}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Controller
                                control={control}
                                name="name"
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label={t("common.name")}
                                        fullWidth
                                        size="small"
                                        error={!!localErrors.name}
                                        helperText={translateErrorMessage(t, localErrors.name)}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Controller
                                control={control}
                                name="report_date"
                                render={({ field: { value, onChange, ...field } }) => (
                                    <DatePicker
                                        {...field}
                                        value={value ? dayjs(value as unknown as string) : null}
                                        onChange={(date, context) => {
                                            onChange(date, context)
                                            recomputeAllEntries()
                                        }}
                                        label={t("common.reportDate")}
                                        format="DD-MM-YYYY"
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                size: "small",
                                                error: !!localErrors.report_date,
                                                helperText: translateErrorMessage(t, localErrors.report_date),
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* ── Entries ── */}
                <Deferred
                    data="general_info"
                    fallback={
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={52} />)}
                        </Box>
                    }
                >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                    <Typography variant="h6">
                        {t("common.entries")}
                        {entryFields.length > 0 && (
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                ({entryFields.length})
                            </Typography>
                        )}
                    </Typography>
                    <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddEntry}>
                        {t("common.addEntry")}
                    </Button>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {entryFields.map((_field, fieldIndex) => {
                        const entryId = getValues().general_info_entries[fieldIndex]?.id
                        const entryKey = typeof entryId === "number" && entryId > 0 ? entryId : `new-${fieldIndex}`
                        return (
                            <EntryAccordion
                                key={entryKey}
                                control={control}
                                fieldIndex={fieldIndex}
                                employees={employees}
                                employeesLoading={employeesLoading}
                                reportDate={reportDate}
                                onRemove={handleRemoveEntry}
                                onEmployeeChange={handleEmployeeChange}
                                onSalaryChange={handleSalaryChange}
                            />
                        )
                    })}

                    {entryFields.length === 0 && (
                        <Paper
                            variant="outlined"
                            sx={{
                                py: 6,
                                textAlign: "center",
                                color: "text.disabled",
                                borderStyle: "dashed",
                            }}
                        >
                            <Typography variant="body2">{t("generalInfos.form.noEntries")}</Typography>
                        </Paper>
                    )}
                </Box>

                {entryFields.length > 0 && (
                    <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddEntry}>
                            {t("common.addEntry")}
                        </Button>
                    </Box>
                )}
                </Deferred>
            </Container>
        </>
    )
}
